import { Link } from "react-router-dom";
import { Mail, MapPin, Phone } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { CONTACT } from "@/data/content";

const EXPLORE = [
  { to: "/about", label: "About us" },
  { to: "/services", label: "Our services" },
  { to: "/how-it-works", label: "How it works" },
  { to: "/testimonials", label: "Stories" },
  { to: "/faqs", label: "FAQs" },
];

const TOGETHER = [
  { to: "/referrals", label: "Referral partners" },
  { to: "/careers", label: "Become a befriender" },
  { to: "/contact", label: "Book a free consultation" },
];

export const Footer = () => (
  <footer data-testid="site-footer" className="bg-forest-deep text-linen/80">
    <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 md:grid-cols-2 lg:grid-cols-4 lg:py-20 lg:px-8">
      <div>
        <div className="flex items-center gap-2.5">
          <span className="block h-11 w-11">
            <LogoMark className="h-11 w-11" />
          </span>
          <span className="leading-none">
            <span className="block font-serif text-xl font-semibold text-linen">Brightside</span>
            <span className="block text-[10px] font-semibold uppercase tracking-[0.28em] text-sage-light">Befriending · Glasgow & Ayr</span>
          </span>
        </div>
        <p className="mt-5 max-w-xs text-sm leading-relaxed text-linen/60">
          Private companionship and befriending for older adults across Glasgow and the surrounding areas. Every befriender is PVG checked.
        </p>
      </div>

      <div>
        <h4 className="text-xs font-semibold uppercase tracking-[0.22em] text-terracotta-soft">Explore</h4>
        <ul className="mt-5 space-y-3 text-sm">
          {EXPLORE.map((l) => (
            <li key={l.to}>
              <Link data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} to={l.to} className="transition-colors hover:text-terracotta-soft">
                {l.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h4 className="text-xs font-semibold uppercase tracking-[0.22em] text-terracotta-soft">Together</h4>
        <ul className="mt-5 space-y-3 text-sm">
          {TOGETHER.map((l) => (
            <li key={l.to}>
              <Link data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} to={l.to} className="transition-colors hover:text-terracotta-soft">
                {l.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h4 className="text-xs font-semibold uppercase tracking-[0.22em] text-terracotta-soft">Say hello</h4>
        <ul className="mt-5 space-y-4 text-sm">
          <li className="flex items-start gap-3">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-sage-light" />
            <span data-testid="footer-address">{CONTACT.address}</span>
          </li>
          <li className="flex items-center gap-3">
            <Mail className="h-4 w-4 shrink-0 text-sage-light" />
            <a data-testid="footer-email-link" href={`mailto:${CONTACT.email}`} className="transition-colors hover:text-terracotta-soft">
              {CONTACT.email}
            </a>
          </li>
          <li className="flex items-start gap-3">
            <Phone className="mt-0.5 h-4 w-4 shrink-0 text-sage-light" />
            <span data-testid="footer-phone">
              {CONTACT.phone} <span className="text-linen/45">· {CONTACT.phoneNote} · {CONTACT.hours}</span>
              <span className="mt-1 block" data-testid="footer-mobile">{CONTACT.mobile} <span className="text-linen/45">· {CONTACT.mobileNote}</span></span>
            </span>
          </li>
        </ul>
      </div>
    </div>

    <div className="border-t border-linen/10">
      <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-3 px-4 py-6 text-xs text-linen/45 sm:flex-row sm:items-center sm:px-6 lg:px-8">
        <p data-testid="footer-legal">
          © 2026 {CONTACT.company} · Registered in Scotland · Covering {CONTACT.areas.join(", ")}
        </p>
        <Link data-testid="footer-team-login-link" to="/admin" className="transition-colors hover:text-terracotta-soft">
          Team login
        </Link>
      </div>
    </div>
  </footer>
);
